import React, { useState } from 'react';
import { Users, CheckSquare, ShieldCheck, Layers, AlertTriangle, Scan, TrendingUp, Activity } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import { todayStats, analyticsData, actionLogs, employees, stores } from '../../data/mockData';

function KpiCard({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: '20px 22px',
      border: '1px solid #F0F0F0', display: 'flex', gap: 14, alignItems: 'flex-start',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={20} color={color} />
      </div>
      <div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E', lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 12, color: color, fontWeight: 600, marginTop: 4 }}>{sub}</div>}
      </div>
    </div>
  );
}

const actionTypeLabels = {
  scan: 'Сканирование',
  osg_check: 'ОСГ проверка',
  display_complete: 'Выкладка',
  photo_upload: 'Фото-отчёт',
  task_accept: 'Принята задача',
  task_cancel: 'Отмена задачи',
};

const actionTypeBadge = {
  scan: 'primary',
  osg_check: 'osg',
  display_complete: 'display',
  photo_upload: 'success',
  task_accept: 'in_progress',
  task_cancel: 'error',
};

export default function Dashboard() {
  const [period, setPeriod] = useState('today');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#1A1A2E' }}>Дашборд</h1>
          <p style={{ color: '#8E8E93', fontSize: 13, marginTop: 2 }}>Обзор состояния сети • 10 апреля 2024</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {['today','week','month'].map(p => (
            <button key={p} onClick={() => setPeriod(p)} style={{
              padding: '7px 14px', borderRadius: 8, border: '1px solid #E5E7EB',
              background: period === p ? '#4A6CF7' : '#fff', color: period === p ? '#fff' : '#6B7280',
              fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>
              {{ today: 'Сегодня', week: 'Неделя', month: 'Месяц' }[p]}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
        <KpiCard icon={Users} label="Сотрудников / Онлайн" value={`${todayStats.totalEmployees} / ${todayStats.onlineNow}`} sub={`${todayStats.onlineNow} онлайн`} color="#4A6CF7" bg="#EEF2FF" />
        <KpiCard icon={CheckSquare} label="Задач сегодня / Выполнено" value={`${todayStats.totalTasksToday} / ${todayStats.completedTasksToday}`} sub={`${todayStats.completionPercent}% выполнено`} color="#34C759" bg="#F0FFF4" />
        <KpiCard icon={ShieldCheck} label="ОСГ проверки" value={todayStats.osgTasks} sub="задач на сегодня" color="#FF6B6B" bg="#FFF0F0" />
        <KpiCard icon={Layers} label="Выкладка" value={todayStats.displayTasks} sub="задач на сегодня" color="#FFBE0B" bg="#FFFBEB" />
        <KpiCard icon={AlertTriangle} label="Критический срок" value={todayStats.criticalExpiry} sub="товаров ≤3 дней" color="#FF9500" bg="#FFF7ED" />
        <KpiCard icon={Scan} label="Сканирований" value={todayStats.scansToday} sub="за сегодня" color="#8B5CF6" bg="#F5F3FF" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        <Card title="Активность за 30 дней" extra={<TrendingUp size={16} color="#4A6CF7" />}>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={analyticsData.dailyData.slice(-14)} margin={{ top: 5, right: 10, bottom: 5, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #F0F0F0' }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="scans" stroke="#4A6CF7" strokeWidth={2} dot={false} name="Сканирования" />
              <Line type="monotone" dataKey="osgChecks" stroke="#FF6B6B" strokeWidth={2} dot={false} name="ОСГ проверки" />
              <Line type="monotone" dataKey="displays" stroke="#FFBE0B" strokeWidth={2} dot={false} name="Выкладки" />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Типы задач" extra={<Activity size={16} color="#4A6CF7" />}>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={analyticsData.taskTypes} cx="50%" cy="50%" innerRadius={55} outerRadius={80}
                paddingAngle={3} dataKey="value" nameKey="name">
                {analyticsData.taskTypes.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Top employees chart */}
      <Card title="Топ-5 активных сотрудников">
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={analyticsData.topEmployees} layout="vertical" margin={{ top: 0, right: 20, left: 100, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: '#9CA3AF' }} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#374151' }} width={100} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
            <Bar dataKey="tasks" name="Задач выполнено" radius={[0, 4, 4, 0]}>
              {analyticsData.topEmployees.map((e, i) => <Cell key={i} fill={e.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Live feed */}
      <Card title="Последние действия" extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#34C759', animation: 'pulse 2s infinite' }} />
          <span style={{ fontSize: 12, color: '#34C759', fontWeight: 600 }}>Live</span>
        </div>
      }>
        <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F8F9FB' }}>
                {['Время','Сотрудник','Магазин','Действие','Товар','Статус'].map(h => (
                  <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #F0F0F0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {actionLogs.map(log => {
                const emp = employees.find(e => e.id === log.employeeId);
                const store = stores.find(s => s.id === log.storeId);
                return (
                  <tr key={log.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '11px 14px', color: '#8E8E93', whiteSpace: 'nowrap' }}>
                      {new Date(log.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Avatar name={emp?.name || '?'} size={28} />
                        <span style={{ fontWeight: 500 }}>{emp?.name || log.employeeId}</span>
                      </div>
                    </td>
                    <td style={{ padding: '11px 14px', color: '#6B7280' }}>{store?.name || log.storeId}</td>
                    <td style={{ padding: '11px 14px' }}>
                      <Badge variant={actionTypeBadge[log.type] || 'gray'}>{actionTypeLabels[log.type] || log.type}</Badge>
                    </td>
                    <td style={{ padding: '11px 14px', color: '#374151', maxWidth: 200 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.product || '—'}</div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <Badge variant={log.result === 'success' ? 'success' : 'error'}>
                        {log.result === 'success' ? 'Успешно' : 'Ошибка'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
